import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, Grid3X3, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import TaskForm from "../components/TaskForm";
import TaskRow, { PRIORITY_META } from "../components/TaskRow";
import AddCicloButton from "../components/AddCicloButton";
import MettiInAgendaModal from "../components/MettiInAgendaModal";

const GROUP_KEY = "pxos.grouping.tasks";
// Bug 4/50 — canonical rule with Piera:
// EVENT-family types are point-in-time calendar entries (webinar, call,
// meeting, appointment). They live in Agenda only, never in "Cicli".
// A ciclo/processo remains a ciclo until it is completed/cancelled.
const EVENT_TYPES = new Set(["event", "call", "meeting", "appointment"]);

export default function Tasks({ inboxOnly = false, scheduledOnly = false }) {
  const [tasks, setTasks] = useState([]);
  const [areas, setAreas] = useState([]);
  const [group, setGroup] = useState(() => { try { return localStorage.getItem(GROUP_KEY) || "priority"; } catch { return "priority"; } });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterArea, setFilterArea] = useState("");
  const [filterType, setFilterType] = useState("");
  const [includeCompleted, setIncludeCompleted] = useState(false);
  // MettiInAgenda modal state (Task Cicli · Fase 1)
  const [scheduleTarget, setScheduleTarget] = useState(null);

  useEffect(() => { try { localStorage.setItem(GROUP_KEY, group); } catch {} }, [group]);
  const load = async () => {
    try {
      const params = { include_completed: includeCompleted };
      if (filterArea) params.area_id = filterArea;
      if (filterType) params.type = filterType;
      const [t, a] = await Promise.all([api.listTasks(params), api.listAreas()]);
      let filtered = t;
      if (inboxOnly) {
        // "Da aprire": ciclo/processo open AND no dates. Escludi eventi puntuali
        // e le AZIONI figlie di cicli (Bug 18/50 · parent_task_id NOT NULL).
        filtered = t.filter((x) => !EVENT_TYPES.has(x.type)
          && !x.parent_task_id
          && !x.scheduled_date && !x.due_date
          && x.status !== "completed" && x.status !== "cancelled");
      } else if (scheduledOnly) {
        // "Aperti": ciclo/processo open con almeno una data. Escludi eventi puntuali
        // (un webinar sabato prossimo non è un ciclo aperto) e le azioni figlie.
        filtered = t.filter((x) => !EVENT_TYPES.has(x.type)
          && !x.parent_task_id
          && (x.scheduled_date || x.due_date)
          && x.status !== "completed" && x.status !== "cancelled");
      }
      setTasks(filtered); setAreas(a);
    } catch { toast.error("Errore"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterArea, filterType, includeCompleted, inboxOnly, scheduledOnly]);

  const areaById = useMemo(() => Object.fromEntries(areas.map((a) => [a.id, a])), [areas]);

  const complete = async (t) => {
    try {
      const res = await api.completeTask(t.id);
      if (res.next_occurrence) toast.success(`✓ Completato — Prossima occorrenza: ${new Date(res.next_occurrence).toLocaleDateString("it-IT")}`);
      else toast.success("✓ Completato");
      load();
    } catch { toast.error("Errore"); }
  };
  const trash = async (t) => {
    if (!confirm("Spostare nel cestino? Sarà eliminato definitivamente dopo 30 giorni.")) return;
    try { await api.trashTask(t.id); toast.success("Nel cestino"); load(); } catch { toast.error("Errore"); }
  };
  const archive = async (t) => {
    try { await api.archiveTask(t.id); toast.success("Archiviato"); load(); } catch { toast.error("Errore"); }
  };

  // In "Da aprire" mode → default click behavior opens the schedule modal
  // (Piera's flow: metti in agenda). Full TaskForm remains accessible via the
  // row's edit action.
  const onRowClick = (t) => {
    if (inboxOnly || scheduledOnly) { setScheduleTarget(t); }
  };

  const pageTitle = inboxOnly ? "Cicli da aprire" : scheduledOnly ? "Cicli aperti" : "Cicli";
  const pageSubtitle = inboxOnly
    ? "Idee e cicli senza data ancora da pianificare. Clic su un ciclo per metterlo in agenda."
    : scheduledOnly
      ? "Cicli in corso con data programmata o scadenza. Clic su un ciclo per riprogrammarlo."
      : "Tutti i cicli in un unico posto.";
  const emptyMsg = inboxOnly
    ? "Nessun ciclo da aprire — perfetto! 🎯"
    : scheduledOnly
      ? "Nessun ciclo in agenda. Aggiungine uno o programma i cicli 'da aprire'."
      : "Nessuna attività. Crea la prima!";

  const grouped = useMemo(() => {
    const groups = {};
    for (const t of tasks) {
      let key, label, order;
      if (group === "priority") {
        key = t.priority; label = PRIORITY_META[key]?.label || key;
        order = { critical: 0, high: 1, medium: 2, low: 3 }[key] ?? 9;
      } else if (group === "area") {
        key = t.area_id || "no-area";
        label = t.area_id ? (areaById[t.area_id]?.name || "?") : "Senza area";
        order = t.area_id ? (areaById[t.area_id]?.sort_order ?? 99) : 999;
      } else {
        key = t.scheduled_date || t.due_date || "no-date";
        label = key === "no-date" ? "Senza data" : new Date(key).toLocaleDateString("it-IT");
        order = key;
      }
      groups[key] ??= { label, tasks: [], order };
      groups[key].tasks.push(t);
    }
    return Object.values(groups).sort((a, b) => (a.order > b.order ? 1 : -1));
  }, [tasks, group, areaById]);

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Operatività</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">{pageTitle}</h1>
          <p className="mt-2 text-sm opacity-80 max-w-2xl">{pageSubtitle}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Link to="/tasks/matrix" className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1" data-testid="matrix-link" title="Apri Matrice Urgente/Importante"><Grid3X3 size={14}/> Matrice</Link>
          <a href={api.reportTasksPdfUrl()} target="_blank" rel="noreferrer" className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1" data-testid="tasks-pdf-btn" title="Esporta lista cicli in PDF"><FileDown size={14}/> PDF</a>
          <AddCicloButton onSaved={load} testid="add-ciclo-btn" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select className="pxos-input w-56" value={filterArea} onChange={(e) => setFilterArea(e.target.value)} data-testid="tasks-filter-area">
          <option value="">Tutte le aree</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select className="pxos-input w-56" value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="tasks-filter-type">
          <option value="">Tutti i tipi</option>
          <option value="task">Ciclo</option>
          <option value="deadline">Scadenza</option>
          <option value="urgency">Urgenza</option>
          {/* Bug 22/50 · Eventi puntuali NON sono cicli (Bug 4/13/18 preservato).
              In vista Cicli il filtro non deve mai proporre event/call/meeting/appointment.
              Restano disponibili solo nella vista "Tutti i cicli" (senza scope), dove
              retro-compat con la vista storica /tasks continua a funzionare. */}
          {!inboxOnly && !scheduledOnly && (
            <>
              <option value="event">Evento</option>
              <option value="call">Call</option>
              <option value="meeting">Meeting</option>
              <option value="appointment">Appuntamento</option>
            </>
          )}
          <option value="payment_reminder">Pagamento</option>
          <option value="followup">Follow-up</option>
          <option value="habit">Abitudine</option>
          <option value="note">Nota</option>
        </select>
        {!inboxOnly && !scheduledOnly && (
          <label className="text-sm flex items-center gap-1">
            <input type="checkbox" checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)} data-testid="toggle-completed"/>
            Includi completati
          </label>
        )}
        <div className="ml-auto inline-flex rounded-full border overflow-hidden" data-testid="grouping-switch">
          {[{ v: "area", label: "Per area" }, { v: "priority", label: "Per priorità" }, { v: "date", label: "Cronologico" }].map((g) => (
            <button key={g.v} onClick={() => setGroup(g.v)}
                    className={`px-3 py-1.5 text-xs font-semibold ${group === g.v ? "bg-white shadow-inner" : "opacity-70 hover:bg-white/70"}`}
                    style={{ color: "var(--pxos-text-dark)" }} data-testid={`grouping-${g.v}`}
                    title={`Raggruppa i task: ${g.label}`}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {grouped.length === 0 ? (
        <div className="pxos-card p-10 text-center opacity-70" data-testid="tasks-empty">{emptyMsg}</div>
      ) : (
        <div className="space-y-6">
          {grouped.map((g) => (
            <section key={g.label} className="pxos-card p-3" data-testid="tasks-group">
              <h2 className="font-display text-lg font-semibold px-2 py-1" data-testid="tasks-group-label">{g.label}</h2>
              <div className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
                {g.tasks.map((t) => (
                  <TaskRow
                    key={t.id} task={t} area={areaById[t.area_id]}
                    onClick={onRowClick}
                    scheduleAction={
                      (inboxOnly || scheduledOnly)
                        ? { label: inboxOnly ? "Metti in agenda" : "Sposta", icon: CalendarClock,
                             onClick: (task) => setScheduleTarget(task) }
                        : undefined
                    }
                    onComplete={complete} onEdit={(x) => { setEditing(x); setFormOpen(true); }}
                    onArchive={archive} onDelete={trash} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <TaskForm open={formOpen} initial={editing} onClose={() => setFormOpen(false)} onSaved={load} />
      <MettiInAgendaModal
        open={!!scheduleTarget} task={scheduleTarget}
        onClose={() => setScheduleTarget(null)}
        onSaved={() => { setScheduleTarget(null); load(); }}
        title={scheduledOnly && scheduleTarget?.scheduled_date ? "Sposta ciclo" : "Metti in agenda"}
      />
    </div>
  );
}
